import { MONTH_IN_DAYS, WEEK_IN_DAYS, YEAR_IN_DAYS } from 'core/common/constants';
import { ToEntity } from 'core/common/mappers';
import { formatPrice } from 'core/common/utils/formatPrice';
import { isTruthyValue } from 'core/common/utils/isTruthyValue';
import { minorUnitsToMajor } from 'core/common/utils/money';
import { roundToDecimals } from 'core/common/utils/number';
import { uniq } from 'core/common/utils/uniq';
import { LocalizationService } from 'core/localization/services';
import { convertCurrencyToSymbol, CurrencySymbol } from 'core/payments/entities';
import {
  AnyOffersAttributes,
  BillingPeriod,
  BillingPeriodUnit,
  emptyOffers,
  isLegacyOffersAttributes,
  LegacyOffersAttributes,
  Offer,
  OfferContentBase,
  OfferGroup,
  offerGroups,
  Offers,
  OffersByCategory,
  OffersGroupsByName,
  offersAttributeMetaKeys,
  OfferWithContentBase,
  PaymentAction,
} from '../entities';
import { GroupedOfferDto, OfferPriceDto, PurchaseOptionDto } from '../services/dto';

type LegacyUpsellKeys = Partial<Record<keyof OffersByCategory, string>>;

// TODO: Remove once every funnel declares upsells in the offers config
const legacyUpsellKeys: LegacyUpsellKeys = {
  numerologyAnalysisUpsell: 'NUMEROLOGY_ANALYSIS',
  tarotReadingUpsell: 'TAROT_READING',
  palmistryGuideUpsell: 'PALMISTRY_GUIDE',
  ultraPackUpsell: 'ULTRA_PACK',
  trialCreditsUpsell: 'TRIAL_CREDITS',
  premiumUpsell: 'PREMIUM',
  natalChartUpsell: 'NATAL_CHART',
  blackMoonUpsell: 'BLACK_MOON',
  consultationUpsell: 'CONSULTATION',
  readingsPackUpsellOffer: 'READINGS_PACK',
};

// TODO: Remove together with legacyUpsellKeys
const legacyDiscountUpsellKeys: LegacyUpsellKeys = {
  discountNumerologyAnalysisUpsell: 'NUMEROLOGY_ANALYSIS',
  discountTarotReadingUpsell: 'TAROT_READING',
  discountPalmistryUpsell: 'PALMISTRY_GUIDE',
  discountUltraPackUpsell: 'ULTRA_PACK',
  discountConsultationUpsell: 'CONSULTATION',
};

export class OfferMapper implements ToEntity<Offer | null, GroupedOfferDto> {
  private readonly paymentProvider = 'solidgate';

  private readonly localizationService = new LocalizationService();

  constructor(private readonly locale?: string) {
    this.toEntity = this.toEntity.bind(this);
  }

  toEntity(dto: GroupedOfferDto): Offer | null {
    const option = this.findPaymentProviderOption(dto);

    if (!option) {
      return null;
    }

    const currencySymbol = convertCurrencyToSymbol(option.price.currency);
    const price = this.convertPriceToMajorUnits(option.price);
    const trialPrice = this.getTrialPrice(option);

    return {
      id: option.external_id,
      offerId: option.id,
      description: dto.display.description,
      period: this.getBillingPeriod(option.price.billing_period),
      trialPeriod: this.getBillingPeriod(option.trial_price?.billing_period),
      currency: option.price.currency,
      currencySymbol,
      minorUnits: option.price.minor_units,
      price,
      pricesPerPeriod: this.getPricePerPeriod(price, option.price.billing_period),
      priceWithCurrency: this.getPriceWithCurrency(price, currencySymbol),
      trial: !!option.trial_price,
      trialPrice,
      trialPricesPerPeriod: this.getPricePerPeriod(trialPrice, option.trial_price?.billing_period),
      trialPriceWithCurrency: this.getPriceWithCurrency(trialPrice, currencySymbol),
      type: dto.display.product_type ?? '',
      creditsAmount: dto.display.credits_amount ?? 0,
    };
  }

  matchOffersWithContent<TContent extends OfferContentBase>(
    offers: Array<Offer>,
    contentOffers?: Array<TContent>,
  ) {
    if (!contentOffers) {
      return offers as Array<OfferWithContentBase<TContent>>;
    }

    const sortedOffers = [...offers].sort((a, b) => {
      const aIdx = contentOffers.findIndex((item) => item.offerIdsToMatch.includes(a.offerId));
      const bIdx = contentOffers.findIndex((item) => item.offerIdsToMatch.includes(b.offerId));

      const aIdxSafe = aIdx === undefined || aIdx === -1 ? 1 : aIdx;
      const bIdxSafe = bIdx === undefined || bIdx === -1 ? 1 : bIdx;

      return aIdxSafe - bIdxSafe;
    });

    return sortedOffers.map((offer) => {
      const content = contentOffers.find((item) => item.offerIdsToMatch.includes(offer.offerId));

      if (!content) {
        return offer as OfferWithContentBase<TContent>;
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { offerIdsToMatch, ...contentWithoutIds } = content;

      return {
        ...offer,
        ...contentWithoutIds,
      };
    });
  }

  mapAttributesToUniqueOfferIds(attributes: Array<AnyOffersAttributes>): Array<string> {
    const allOfferIds = attributes.flatMap((attribute) => {
      if (isLegacyOffersAttributes(attribute)) {
        return this.mapLegacyAttributeToOfferIds(attribute);
      }

      return offerGroups.flatMap((group) => Object.values(attribute[group]));
    });

    return uniq(allOfferIds);
  }

  mapAttributesToOffers(
    attributes: Array<AnyOffersAttributes>,
    offers: Array<Offer>,
  ): Array<Offers> {
    return attributes.map((attribute) => {
      // TODO: Remove this once we have a native offers attributes
      if (isLegacyOffersAttributes(attribute)) {
        return this.mapLegacyAttributeToOffers(attribute, offers);
      }

      const { condition, feature, ...groups } = attribute;

      const nativeOffers: Offers = {
        ...emptyOffers,
        ...this.mapOffersToGroups(offers, groups),
        condition,
        feature,
      };

      return nativeOffers;
    });
  }

  private mapLegacyAttributeToOffers(
    attribute: LegacyOffersAttributes,
    offers: Array<Offer>,
  ): Offers {
    const { condition, feature, ...categories } = attribute;

    const discountDefaultOffers = this.findOffersByIdPrefix(
      offers,
      categories.discountDefaultOffers,
    );
    const discountOffer = this.findOfferByIdPrefix(offers, categories.discountOffer);

    const legacyOffers: Offers = {
      defaultOffers: this.findOffersByIdPrefix(offers, categories.defaultOffers),
      timeUpOffers: this.findOffersByIdPrefix(offers, categories.timeUpOffers),
      discountOffers: discountDefaultOffers.length
        ? discountDefaultOffers
        : [discountOffer].filter(isTruthyValue),
      upsells: this.mapLegacyUpsells(offers, categories, legacyUpsellKeys),
      discountUpsells: this.mapLegacyUpsells(offers, categories, legacyDiscountUpsellKeys),
      condition,
      feature,
    };

    return legacyOffers;
  }

  // TODO: Remove together with the legacy attributes form
  private mapLegacyAttributeToOfferIds(attribute: LegacyOffersAttributes): Array<string> {
    const categories = Object.entries(attribute).filter(
      ([key]) => !offersAttributeMetaKeys.includes(key),
    );

    return categories.flatMap(([, offerIds]) => offerIds as string | Array<string>);
  }

  private findPaymentProviderOption(dto: GroupedOfferDto) {
    return dto.purchase_options.find((option) => option.provider === this.paymentProvider);
  }

  private mapOffersToGroups(
    offers: Array<Offer>,
    groups: OffersGroupsByName,
  ): Pick<Offers, OfferGroup> {
    return {
      defaultOffers: this.mapGroupToOfferList(offers, groups.defaultOffers),
      timeUpOffers: this.mapGroupToOfferList(offers, groups.timeUpOffers),
      discountOffers: this.mapGroupToOfferList(offers, groups.discountOffers),
      upsells: this.mapGroupToOfferMap(offers, groups.upsells),
      discountUpsells: this.mapGroupToOfferMap(offers, groups.discountUpsells),
    };
  }

  private mapGroupToOfferList(offers: Array<Offer>, group: Record<string, string>): Array<Offer> {
    const offerIds = Object.values(group);

    const groupOffers = offerIds.flatMap((offerId) => {
      const offer = this.findOfferById(offers, offerId);

      return offer ? [offer] : [];
    });

    return groupOffers;
  }

  private mapGroupToOfferMap(
    offers: Array<Offer>,
    group: Record<string, string>,
  ): Record<string, Offer> {
    const groupEntries = Object.entries(group).flatMap(([offerKey, offerId]) => {
      const offer = this.findOfferById(offers, offerId);

      return offer ? [[offerKey, offer] as const] : [];
    });

    return Object.fromEntries(groupEntries);
  }

  // TODO: Remove together with legacyUpsellKeys
  private mapLegacyUpsells(
    offers: Array<Offer>,
    categories: OffersByCategory,
    legacyKeys: LegacyUpsellKeys,
  ): Record<string, Offer> {
    const upsellEntries = Object.entries(legacyKeys).flatMap(([legacyKey, offerKey]) => {
      const offerId = categories[legacyKey as keyof OffersByCategory];
      const offer = typeof offerId === 'string' ? this.findOfferByIdPrefix(offers, offerId) : null;

      return offer ? [[offerKey, offer] as const] : [];
    });

    return Object.fromEntries(upsellEntries);
  }

  private findOfferByIdPrefix(offers: Array<Offer>, offerId?: string): Offer | null {
    if (!offerId) {
      return null;
    }

    const offer = offers.find((item) => item.offerId.startsWith(offerId));

    return offer ?? null;
  }

  private findOffersByIdPrefix(offers: Array<Offer>, offerIds?: Array<string>): Array<Offer> {
    if (!offerIds) {
      return [];
    }

    const matchedOffers = offers.filter((offer) =>
      offerIds.some((offerId) => offer.offerId.startsWith(offerId)),
    );

    return matchedOffers;
  }

  private findOfferById(offers: Array<Offer>, offerId: string): Offer | undefined {
    return offers.find((offer) => offer.offerId === offerId);
  }

  private getPricePerPeriod(price: number, period?: BillingPeriod) {
    if (!price) return { day: 0, week: 0, month: 0 };

    switch (period?.unit) {
      case BillingPeriodUnit.DAY:
        return {
          day: roundToDecimals(price / period.value),
          week: roundToDecimals((price / period.value) * WEEK_IN_DAYS),
          month: roundToDecimals((price / period.value) * MONTH_IN_DAYS),
        };
      case BillingPeriodUnit.WEEK:
        return {
          day: roundToDecimals(price / (period.value * WEEK_IN_DAYS)),
          week: roundToDecimals(price / period.value),
          month: roundToDecimals(((price / period.value) * MONTH_IN_DAYS) / WEEK_IN_DAYS),
        };
      case BillingPeriodUnit.MONTH:
        return {
          day: roundToDecimals(price / (period.value * MONTH_IN_DAYS)),
          week: roundToDecimals(price / ((period.value * MONTH_IN_DAYS) / WEEK_IN_DAYS)),
          month: roundToDecimals(price / period.value),
        };
      case BillingPeriodUnit.YEAR:
        return {
          day: roundToDecimals(price / (period.value * YEAR_IN_DAYS)),
          week: roundToDecimals(price / ((period.value * YEAR_IN_DAYS) / WEEK_IN_DAYS)),
          month: roundToDecimals(price / ((period.value * YEAR_IN_DAYS) / MONTH_IN_DAYS)),
        };
      default:
        return {
          day: price,
          week: price,
          month: price,
        };
    }
  }

  private getPriceWithCurrency(price: number, currencySymbol: CurrencySymbol): string {
    return this.localizationService.isRtl
      ? `${formatPrice(price)}${currencySymbol}`
      : `${currencySymbol}${formatPrice(price)}`;
  }

  private getTrialPrice(option: PurchaseOptionDto) {
    if (option.trial_payment_action === PaymentAction.AUTH_VOID) {
      return 0;
    }

    return this.convertPriceToMajorUnits(option.trial_price);
  }

  private convertPriceToMajorUnits(price?: OfferPriceDto) {
    if (!price) {
      return 0;
    }

    return minorUnitsToMajor(price.amount, price.minor_units);
  }

  private getBillingPeriod(billingPeriod?: BillingPeriod) {
    if (!billingPeriod) {
      return '';
    }

    return `${billingPeriod.value} ${billingPeriod.unit}${billingPeriod.value > 1 ? 's' : ''}`;
  }
}
